<template>
  <div class="p-6 bg-gray-50 dark:bg-gray-800 min-h-screen">
    <div class="max-w-4xl mx-auto">
      <LoadingSpinner :loading="loading" message="Loading settings..." />
      <Toast ref="toast" />

      <h1 class="text-2xl font-bold mb-6 dark:text-white">Application Settings</h1>
      
      <!-- Settings Tabs -->
      <div class="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex space-x-4">
          <button 
            @click="activeTab = 'appearance'" 
            class="py-2 px-4 font-medium text-sm transition-colors"
            :class="activeTab === 'appearance' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          >
            Appearance
          </button>
          <button 
            @click="activeTab = 'map'" 
            class="py-2 px-4 font-medium text-sm transition-colors"
            :class="activeTab === 'map' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          >
            Map Preferences
          </button>
          <button 
            @click="activeTab = 'tools'" 
            class="py-2 px-4 font-medium text-sm transition-colors"
            :class="activeTab === 'tools' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          >
            Tools
          </button>
          <button 
            @click="activeTab = 'accessibility'" 
            class="py-2 px-4 font-medium text-sm transition-colors"
            :class="activeTab === 'accessibility' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          >
            Accessibility
          </button>
          <button 
            @click="activeTab = 'account'" 
            class="py-2 px-4 font-medium text-sm transition-colors"
            :class="activeTab === 'account' ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          >
            Account
          </button>
        </div>
      </div>
      
      <!-- Appearance Settings -->
      <div v-if="activeTab === 'appearance'" class="space-y-6">
        <div class="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4 dark:text-white">Theme</h2>
          <div class="flex flex-wrap gap-4">
            <button 
              @click="settings.theme = 'light'"
              class="flex-1 p-4 rounded-lg text-center border-2 transition-colors min-w-[130px]"
              :class="settings.theme === 'light' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
            >
              <div class="h-24 mb-2 bg-white border border-gray-200 rounded-md shadow-sm flex items-center justify-center text-black">
                Light Mode Preview
              </div>
              <span class="font-medium">Light</span>
            </button>
            <button 
              @click="settings.theme = 'dark'"
              class="flex-1 p-4 rounded-lg text-center border-2 transition-colors min-w-[130px]"
              :class="settings.theme === 'dark' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
            >
              <div class="h-24 mb-2 bg-gray-800 border border-gray-700 rounded-md shadow-sm flex items-center justify-center text-white">
                Dark Mode Preview
              </div>
              <span class="font-medium">Dark</span>
            </button>
            <button 
              @click="settings.theme = 'system'"
              class="flex-1 p-4 rounded-lg text-center border-2 transition-colors min-w-[130px]"
              :class="settings.theme === 'system' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
            >
              <div class="h-24 mb-2 bg-gradient-to-r from-white to-gray-800 rounded-md shadow-sm flex items-center justify-center">
                <span class="bg-white text-black px-2 py-1 rounded-l">Sys</span>
                <span class="bg-gray-800 text-white px-2 py-1 rounded-r">tem</span>
              </div>
              <span class="font-medium">System</span>
            </button>
          </div>
        </div>
        
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Typography</h2>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
              <div class="flex items-center space-x-4">
                <button 
                  @click="decreaseFontSize" 
                  class="bg-gray-100 text-gray-700 p-2 rounded-md hover:bg-gray-200"
                >
                  <i class="fas fa-minus"></i>
                </button>
                <div class="flex-grow bg-gray-100 rounded-lg h-2">
                  <div 
                    class="h-2 bg-primary-500 rounded-lg" 
                    :style="{ width: `${((settings.fontSize - 12) / 8) * 100}%` }"
                  ></div>
                </div>
                <button 
                  @click="increaseFontSize" 
                  class="bg-gray-100 text-gray-700 p-2 rounded-md hover:bg-gray-200"
                >
                  <i class="fas fa-plus"></i>
                </button>
                <span class="text-sm text-gray-500 w-12 text-center">
                  {{ settings.fontSize }}px
                </span>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
              <select 
                v-model="settings.fontFamily"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="system-ui, sans-serif">System Default</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Lato', sans-serif">Lato</option>
                <option value="'Poppins', sans-serif">Poppins</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Text Preview</label>
              <div 
                class="p-4 border border-gray-200 rounded-md"
                :style="{ 
                  fontSize: `${settings.fontSize}px`, 
                  fontFamily: settings.fontFamily,
                  backgroundColor: settings.theme === 'dark' ? '#1f2937' : 'white',
                  color: settings.theme === 'dark' ? 'white' : '#1f2937'
                }"
              >
                <h2 class="text-lg font-bold mb-2">This is a heading</h2>
                <p>This is how your text will appear with the current settings. GeoProperty Hub allows you to customize the appearance to your preferences.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Language</h2>
          <div>
            <select 
              v-model="settings.language"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
              <option value="sw">Kiswahili</option>
              <option value="ar">العربية</option>
            </select>
            <p class="text-sm text-gray-500 mt-2">
              Application restart required for full language switch.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Map Preferences Settings -->
      <div v-if="activeTab === 'map'" class="space-y-6">
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Map Display</h2>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Default Map View</label>
              <select 
                v-model="settings.defaultMapView"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Show All Properties</option>
                <option value="my">Show My Properties</option>
                <option value="recent">Show Recent Uploads</option>
                <option value="custom">Custom Location</option>
              </select>
            </div>
            
            <div v-if="settings.defaultMapView === 'custom'">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                  <input 
                    v-model.number="settings.defaultLatitude" 
                    type="number" 
                    step="0.000001"
                    class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                  <input 
                    v-model.number="settings.defaultLongitude" 
                    type="number" 
                    step="0.000001"
                    class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2 mt-4">Zoom Level</label>
                <input 
                  v-model.number="settings.defaultZoom" 
                  type="range" 
                  min="1" 
                  max="20" 
                  class="w-full"
                />
                <div class="flex justify-between text-xs text-gray-500">
                  <span>Country</span>
                  <span>{{ settings.defaultZoom }}</span>
                  <span>Building</span>
                </div>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Default Basemap</label>
              <select 
                v-model="settings.defaultBasemap"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="satellite">Satellite</option>
                <option value="osm">OpenStreetMap</option>
                <option value="topo">Topographic</option>
                <option value="street">Street Map</option>
                <option value="terrain">Terrain</option>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Property Display</h2>
          
          <div class="space-y-4">
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.showPropertyLabels" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Show Property Labels</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Display property titles on the map. May become crowded with many properties.
              </p>
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.showPriceOnHover" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Show Price on Hover</span>
              </label>
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.clusterProperties" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Cluster Properties</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Group nearby properties into clusters at lower zoom levels.
              </p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Default Property Style</label>
              <select 
                v-model="settings.defaultPropertyStyle"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="1">Standard Style</option>
                <option value="2">Modern Style</option>
                <option value="3">Minimal Style</option>
                <option value="4">Detailed Style</option>
                <option value="custom">Custom Style</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Tools Settings -->
      <div v-if="activeTab === 'tools'" class="space-y-6">
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Visible Tools</h2>
          
          <div class="space-y-4">
            <p class="text-sm text-gray-500">
              Choose which tools are visible in the toolbar. Drag to reorder.
            </p>
            
            <div class="border border-gray-200 rounded-lg p-2">
              <div 
                v-for="(tool, index) in settings.visibleTools" 
                :key="tool.id"
                class="p-3 bg-white border border-gray-200 rounded-md mb-2 flex items-center cursor-move"
              >
                <div class="mr-2 text-gray-400">
                  <i class="fas fa-grip-vertical"></i>
                </div>
                <div class="flex-grow">
                  <div class="font-medium">{{ tool.name }}</div>
                  <div class="text-xs text-gray-500">{{ tool.description }}</div>
                </div>
                <div>
                  <label class="flex items-center">
                    <input 
                      v-model="tool.visible" 
                      type="checkbox" 
                      class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span class="ml-2 text-sm font-medium text-gray-700">Show</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Measurement Preferences</h2>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Default Measurement Units</label>
              <select 
                v-model="settings.measurementUnits"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="metric">Metric (m/km²)</option>
                <option value="imperial">Imperial (ft/acres)</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Line Color</label>
              <input 
                v-model="settings.measurementLineColor" 
                type="color" 
                class="w-full h-10 p-1 border border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.showMeasurementTooltips" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Show Measurement Tooltips</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Accessibility Settings -->
      <div v-if="activeTab === 'accessibility'" class="space-y-6">
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Visual Accessibility</h2>
          
          <div class="space-y-4">
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.highContrastMode" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">High Contrast Mode</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Increases contrast for better visibility.
              </p>
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.reduceMotion" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Reduce Motion</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Minimizes animations and transitions throughout the app.
              </p>
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.enableZoomControls" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Enable Keyboard Zoom Controls</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Use +/- keys to zoom in and out of the map.
              </p>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Color Vision Deficiency</h2>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Color Filter</label>
              <select 
                v-model="settings.colorFilter"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="none">None</option>
                <option value="protanopia">Protanopia (Red-Blind)</option>
                <option value="deuteranopia">Deuteranopia (Green-Blind)</option>
                <option value="tritanopia">Tritanopia (Blue-Blind)</option>
                <option value="achromatopsia">Achromatopsia (No Color)</option>
              </select>
              <p class="text-xs text-gray-500 mt-1">
                Apply filters to accommodate different types of color vision deficiency.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Account Settings -->
      <div v-if="activeTab === 'account'" class="space-y-6">
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Notification Preferences</h2>
          
          <div class="space-y-4">
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.emailNotifications" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Email Notifications</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Receive updates about new properties and system changes.
              </p>
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.importNotifications" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Data Import Notifications</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Get notified when data imports complete or fail.
              </p>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-6 rounded-lg shadow-sm">
          <h2 class="text-lg font-semibold mb-4">Session Settings</h2>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Auto-Logout After Inactivity</label>
              <select 
                v-model="settings.autoLogoutMinutes"
                class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="0">Never</option>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
              </select>
            </div>
            
            <div>
              <label class="flex items-center">
                <input 
                  v-model="settings.rememberLastView" 
                  type="checkbox" 
                  class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">Remember Last View</span>
              </label>
              <p class="text-xs text-gray-500 mt-1 ml-6">
                Return to the last page you were viewing when you log in again.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Success/Error Messages -->
      <div v-if="error || successMessage" class="mt-8 mb-4">
        <div v-if="error" class="p-4 bg-red-100 text-red-700 rounded-md border border-red-200">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
            {{ error }}
          </div>
        </div>
        <div v-if="successMessage" class="p-4 bg-green-100 text-green-700 rounded-md border border-green-200">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            {{ successMessage }}
          </div>
        </div>
      </div>
      
      <!-- Save and Reset Buttons -->
      <div class="mt-4 flex justify-end space-x-4 sticky bottom-0 p-4 bg-white shadow-md rounded-lg">
        <button 
          @click="resetSettings" 
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          :disabled="loading"
        >
          Reset to Default
        </button>
        <button 
          @click="saveSettings" 
          class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          :disabled="loading"
        >
          <span v-if="loading">
            <i class="fas fa-spinner fa-spin mr-2"></i>
            Saving...
          </span>
          <span v-else>Save Settings</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import Toast from '../components/Toast.vue';

const router = useRouter();
const activeTab = ref('appearance');
const loading = ref(true);
const error = ref(null);
const successMessage = ref(null);
const toast = inject('toast');

// Settings object with all user preferences
const settings = ref({
  // Appearance
  theme: 'light',
  fontSize: 16,
  fontFamily: 'system-ui, sans-serif',
  language: 'en',
  
  // Map Preferences
  defaultMapView: 'all',
  defaultLatitude: -1.2921,
  defaultLongitude: 36.8219,
  defaultZoom: 12,
  defaultBasemap: 'satellite',
  showPropertyLabels: true,
  showPriceOnHover: true,
  clusterProperties: true,
  defaultPropertyStyle: '1',
  
  // Tools
  visibleTools: [
    { id: 1, name: 'Zoom Controls', description: 'Zoom in and out on the map', visible: true },
    { id: 2, name: 'Selection Tool', description: 'Select properties on the map', visible: true },
    { id: 3, name: 'Layer Manager', description: 'Toggle map layers', visible: true },
    { id: 4, name: 'Style Editor', description: 'Edit property styles', visible: true },
    { id: 5, name: 'Measure Tool', description: 'Measure distances and areas', visible: true },
    { id: 6, name: 'Export Tool', description: 'Export map as image or PDF', visible: true },
    { id: 7, name: 'Reset View', description: 'Reset the map to default view', visible: true },
  ],
  measurementUnits: 'metric',
  measurementLineColor: '#3388ff',
  showMeasurementTooltips: true,
  
  // Accessibility
  highContrastMode: false,
  reduceMotion: false,
  enableZoomControls: true,
  colorFilter: 'none',
  
  // Account
  emailNotifications: true,
  importNotifications: true,
  autoLogoutMinutes: 60,
  rememberLastView: true,
});

// Load user settings from the server
const loadSettings = async () => {
  loading.value = true;
  error.value = null;
  successMessage.value = null;
  
  try {
    const response = await axios.get('/api/users/settings');
    if (response.data.success) {
      // Only update settings that exist in the response
      const serverSettings = response.data.settings;
      for (const key in serverSettings) {
        if (key in settings.value) {
          settings.value[key] = serverSettings[key];
        }
      }
    }
    toast.value?.addToast('Settings loaded', 'success');
  } catch (err) {
    console.error('Error loading settings:', err);
    error.value = 'Failed to load settings. Using defaults.';
    toast.value?.addToast(error.value, 'error');
    
    // If API fails, try to load from localStorage
    const savedSettings = localStorage.getItem('user_settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        for (const key in parsedSettings) {
          if (key in settings.value) {
            settings.value[key] = parsedSettings[key];
          }
        }
      } catch (parseErr) {
        console.error('Error parsing saved settings:', parseErr);
      }
    }
  } finally {
    loading.value = false;
  }
};

// Save settings to the server
const saveSettings = async () => {
  loading.value = true;
  error.value = null;
  successMessage.value = null;
  
  try {
    const response = await axios.post('/api/users/settings', { settings: settings.value });
    if (response.data.success) {
      // Save to localStorage as a backup
      localStorage.setItem('user_settings', JSON.stringify(settings.value));
      
      // Apply settings
      applySettings();
      
      successMessage.value = 'Settings saved successfully!';
      toast.value?.addToast(successMessage.value, 'success');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        successMessage.value = null;
      }, 3000);
    }
  } catch (err) {
    console.error('Error saving settings:', err);
    error.value = 'Failed to save settings to server. Your settings have been saved locally.';
    toast.value?.addToast(error.value, 'error');
    
    // Save to localStorage even if API fails
    localStorage.setItem('user_settings', JSON.stringify(settings.value));
    applySettings();
  } finally {
    loading.value = false;
  }
};

// Apply settings to the application
const applySettings = () => {
  // Apply theme
  if (settings.value.theme === 'dark' || 
      (settings.value.theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  
  // Apply font settings
  document.documentElement.style.setProperty('--font-size-base', `${settings.value.fontSize}px`);
  document.documentElement.style.setProperty('--font-family', settings.value.fontFamily);
  
  // Apply high contrast if enabled
  if (settings.value.highContrastMode) {
    document.documentElement.classList.add('high-contrast');
  } else {
    document.documentElement.classList.remove('high-contrast');
  }
  
  // Apply reduced motion if enabled
  if (settings.value.reduceMotion) {
    document.documentElement.classList.add('reduce-motion');
  } else {
    document.documentElement.classList.remove('reduce-motion');
  }
  
  // Apply color filter if selected
  document.documentElement.style.setProperty('--color-filter', settings.value.colorFilter === 'none' ? 'none' : `url(#${settings.value.colorFilter})`);
  
  // Other settings would be applied when different components are loaded
};

// Reset settings to defaults
const resetSettings = () => {
  if (confirm('Are you sure you want to reset all settings to their defaults?')) {
    error.value = null;
    successMessage.value = null;
    
    settings.value = {
      // Appearance
      theme: 'light',
      fontSize: 16,
      fontFamily: 'system-ui, sans-serif',
      language: 'en',
      
      // Map Preferences
      defaultMapView: 'all',
      defaultLatitude: -1.2921,
      defaultLongitude: 36.8219,
      defaultZoom: 12,
      defaultBasemap: 'satellite',
      showPropertyLabels: true,
      showPriceOnHover: true,
      clusterProperties: true,
      defaultPropertyStyle: '1',
      
      // Tools
      visibleTools: [
        { id: 1, name: 'Zoom Controls', description: 'Zoom in and out on the map', visible: true },
        { id: 2, name: 'Selection Tool', description: 'Select properties on the map', visible: true },
        { id: 3, name: 'Layer Manager', description: 'Toggle map layers', visible: true },
        { id: 4, name: 'Style Editor', description: 'Edit property styles', visible: true },
        { id: 5, name: 'Measure Tool', description: 'Measure distances and areas', visible: true },
        { id: 6, name: 'Export Tool', description: 'Export map as image or PDF', visible: true },
        { id: 7, name: 'Reset View', description: 'Reset the map to default view', visible: true },
      ],
      measurementUnits: 'metric',
      measurementLineColor: '#3388ff',
      showMeasurementTooltips: true,
      
      // Accessibility
      highContrastMode: false,
      reduceMotion: false,
      enableZoomControls: true,
      colorFilter: 'none',
      
      // Account
      emailNotifications: true,
      importNotifications: true,
      autoLogoutMinutes: 60,
      rememberLastView: true,
    };
    
    applySettings();
    
    // Show success message
    successMessage.value = 'Settings reset to defaults.';
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = null;
    }, 3000);
  }
};

// Font size helpers
const increaseFontSize = () => {
  if (settings.value.fontSize < 20) {
    settings.value.fontSize += 1;
  }
};

const decreaseFontSize = () => {
  if (settings.value.fontSize > 12) {
    settings.value.fontSize -= 1;
  }
};

// Load settings on component mount
onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
.settings-view {
  min-height: calc(100vh - 64px);
  background-color: #f9fafb;
}
</style>